import type { ReactElement } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import capitalize from "lodash/capitalize";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Type, PlainTooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { StyledAutocompleteDivider } from "app/pages/sales-intelligence/modules/company-autocomplete/components/DomainAutocompleteItem/styles";
import highlightQueryWords from "app/pages/sales-intelligence/modules/company-autocomplete/utils/highlightQueryWords";
import CompanyQuotaIndicator from "app/pages/sales-intelligence/modules/quota/components/CompanyQuotaIndicator/CompanyQuotaIndicator";
import NotEnoughQuotaTooltipContainer from "app/pages/sales-intelligence/modules/quota/containers/NotEnoughQuotaTooltipContainer/NotEnoughQuotaTooltipContainer";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { IAutoCompleteSiteItem, IDomainItem } from "app/@types/autocomplete";

import DomainAutocompleteItem from "../DomainAutocompleteItem/DomainAutocompleteItem";

import {
    StyledAutocompleteCompanyWrapper,
    StyledAutocompleteIconContainer,
    StyledAutocompleteDomainsList,
    StyledAutocompleteItemIcon,
    StyledAutocompleteItemText,
    StyledAutocompleteItemTextWrapper,
} from "./styles";

const STRIP_URL_PREFIX_RE = /^(?:https?:\/\/)?(?:www\.)?/i;
const TRAILING_SLASH_RE = /\/+$/;

export const clearUrlPrefixes = (queryWords: readonly string[]): string[] =>
    queryWords.map((w) => w.trim().replace(STRIP_URL_PREFIX_RE, "").replace(TRAILING_SLASH_RE, "")).filter(Boolean);

const CompanyAutocompleteItem = ({
    company,
    queryWords = [],
    hideLabel = false,
    onCompanyClick,
    onWebsiteClick,
    onCollapse,
    isLocked = false,
    isHideDataCreditChips = false,
}: {
    company: IAutoCompleteSiteItem;
    queryWords?: string[];
    hideLabel?: boolean;
    onCompanyClick?: ({ key, companyName, image }: { key: string; companyName: string; image?: string }) => void;
    onWebsiteClick?: (domain: string, companyName?: string, image?: string) => void;
    onCollapse?: (isCollapsed: boolean, domainLength: number, companyName: string) => void;
    isLocked?: boolean;
    isHideDataCreditChips?: boolean;
}) => {
    const { companyInfo, domainsInfo, hasData, isPurchased = false } = company;
    const companyName = companyInfo?.companyName || "";
    const mainDomain = companyInfo?.mainDomain;

    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const clearedQueryWords = useMemo(() => clearUrlPrefixes(queryWords), [queryWords]);

    const [isCompanyNameMatching, domainsToRender] = useMemo(() => {
        const companyNameWords = companyName.toLowerCase().split(" ").filter(Boolean);
        const isCompanyMatching = clearedQueryWords.every((word) =>
            companyNameWords.some((companyWord) => companyWord.startsWith(word)),
        );
        let filteredDomains: IDomainItem[] = [];
        if (domainsInfo) {
            filteredDomains = isCompanyMatching
                ? domainsInfo
                : domainsInfo.filter((domain) =>
                      clearedQueryWords.some((word) => domain.name.toLowerCase().includes(word)),
                  );
        }
        if (!isCompanyMatching && filteredDomains.length === 0 && domainsInfo) {
            return [false, domainsInfo];
        }
        return [isCompanyMatching, filteredDomains];
    }, [company, clearedQueryWords]);

    const [isCollapsed, setIsCollapsed] = useState(isCompanyNameMatching);
    const iconContainerRef = useRef<HTMLDivElement>(null);
    const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);

    const { data: companyQuota } = useCompanyQuota();

    const wrapWithLockedTrialTooltip = (node: ReactElement): ReactElement =>
        isLocked && companyQuota ? (
            <NotEnoughQuotaTooltipContainer quota={companyQuota}>{node}</NotEnoughQuotaTooltipContainer>
        ) : (
            node
        );

    const setTextRef = (node: HTMLDivElement | null) => {
        if (node) {
            setIsTooltipEnabled(node.scrollWidth > node.clientWidth);
        }
    };

    const handleCompanyClick = (event: React.SyntheticEvent<HTMLElement>) => {
        if (isLocked) {
            return;
        }

        if (iconContainerRef.current && !iconContainerRef.current.contains(event.target as Node) && mainDomain) {
            if (!hasData) {
                return;
            }
            onCompanyClick?.({ key: mainDomain, companyName, image: companyInfo?.image });
        }
    };

    const handleDomainClick = useCallback(
        (domain: string) => {
            if (isLocked) {
                return;
            }
            onWebsiteClick?.(domain, companyName, companyInfo?.image);
        },
        [onWebsiteClick, companyName, companyInfo?.image, isLocked],
    );

    const handleCollapseClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsCollapsed((prev) => !prev);

        onCollapse?.(isCollapsed, domainsToRender?.length || 0, companyName);
    };

    const companyRow = (
        <StyledAutocompleteCompanyWrapper
            onClick={handleCompanyClick}
            isCollapsed={isCollapsed}
            isDisabled={isLocked || !hasData}
        >
            <StyledAutocompleteIconContainer
                onMouseDown={handleCollapseClick}
                ref={iconContainerRef}
            >
                <SWReactIcons
                    iconName={isCollapsed ? "plus-square" : "minus-square"}
                    iconColor={colorsPalettes.carbon[300]}
                />
            </StyledAutocompleteIconContainer>
            <Flex
                alignItems="center"
                overflow="hidden"
                position="relative"
            >
                <StyledAutocompleteItemIcon
                    iconType={Type.Website}
                    iconSrc={companyInfo?.image || ""}
                />
                <PlainTooltip
                    tooltipContent={companyName}
                    placement="top"
                    enabled={isTooltipEnabled}
                >
                    <StyledAutocompleteItemTextWrapper>
                        <StyledAutocompleteItemText
                            ref={setTextRef}
                            isDisabled={!hasData}
                        >
                            {hasData
                                ? highlightQueryWords(companyName, clearedQueryWords)
                                : translate("si.pages.home.search.autocomplete.no_data.unknown")}
                        </StyledAutocompleteItemText>
                    </StyledAutocompleteItemTextWrapper>
                </PlainTooltip>
                {hideLabel ? null : (
                    <>
                        <StyledAutocompleteDivider />
                        <Text
                            fontVariant="listItem3"
                            color={hasData ? colorsPalettes.carbon[250] : colorsPalettes.carbon[200]}
                        >
                            {capitalize(translate("common.company"))}
                        </Text>
                    </>
                )}
            </Flex>
            <Flex
                gap="8px"
                margin="0 0 0 auto"
                alignItems="center"
            >
                {hasData ? null : (
                    <Text
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                    >
                        {capitalize(translate("si.pages.home.search.autocomplete.no_data.company"))}
                    </Text>
                )}
                <CompanyQuotaIndicator
                    isDataCreditsEnabled={isDataCreditsEnabled}
                    isLocked={isLocked}
                    isPurchased={isPurchased}
                    hideDataCreditChips={isHideDataCreditChips}
                />
            </Flex>
        </StyledAutocompleteCompanyWrapper>
    );

    return (
        <>
            {wrapWithLockedTrialTooltip(companyRow)}
            <StyledAutocompleteDomainsList isCollapsed={isCollapsed}>
                {domainsToRender.map((domain, index) => (
                    <DomainAutocompleteItem
                        key={domain.name}
                        domain={domain}
                        queryWords={clearedQueryWords}
                        isFirst={index === 0}
                        isLast={index === (domainsToRender?.length ?? 0) - 1}
                        onItemClick={handleDomainClick}
                        hideLabel={hideLabel}
                        isLocked={isLocked}
                        hasCompanyData={hasData}
                        isHideDataCreditChips={isHideDataCreditChips}
                        isPurchased={isPurchased}
                        companyQuota={companyQuota}
                    />
                ))}
            </StyledAutocompleteDomainsList>
        </>
    );
};

export default CompanyAutocompleteItem;
